/**
 * Uma linha de item dos Primeiros Passos: o estado (sempre com texto), o nome, a medida, o
 * detalhe, quem falta e o link para a tela que resolve (spec #387, seção 5.3).
 *
 * O link nunca promete o que a tela de destino recusa: quem decide é `avaliar`, e o botão que
 * aparece é `RouteActionButton` com o `exige` da tabela de textos. Quando a pessoa só consulta,
 * a linha leva à leitura e diz quem cadastra; quando só a plataforma resolve, não há botão.
 */

import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ChevronRight as GoIcon,
  InfoOutlined as InfoIcon,
  Lock as LockIcon,
  RadioButtonUnchecked as PendingIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';

import { avaliar } from '../../../authorization/pode';
import { RouteActionButton } from '../../../components';
import type { Perspectiva } from '../../../types/manifesto';
import type { ItemState, OnboardingItem } from '../../../types/onboarding';
import type { ManifestoDeAcesso } from '../proximoPasso';
import {
  acaoDoItem,
  escolher,
  FRASE_DA_RESSALVA,
  FRASES,
  ITENS,
  QUEM_RESOLVE,
  ROTULO_DO_MODO,
  TEXTO_DO_ESTADO,
  type Destino,
  type ItemTexto,
} from '../textos';

const CHIP_DO_ESTADO: Record<
  ItemState,
  { cor: 'success' | 'warning' | 'info' | 'default'; icone: React.ReactElement }
> = {
  done: { cor: 'success', icone: <CheckIcon /> },
  warn: { cor: 'warning', icone: <WarnIcon /> },
  pending: { cor: 'default', icone: <PendingIcon /> },
  locked: { cor: 'default', icone: <LockIcon /> },
  info: { cor: 'info', icone: <InfoIcon /> },
  optional: { cor: 'default', icone: <InfoIcon /> },
};

const LETRA_DO_CORPO = '16px';
const AREA_DE_TOQUE = 44;

const plural = (n: number, [um, varios]: readonly [string, string]): string =>
  `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;

/** `2026-11-04` ou `2026-11-04T00:00:00Z` viram `04/11/2026`, sem passar pelo fuso do navegador. */
function dataBrasileira(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

function medidaDoItem(item: OnboardingItem, texto: ItemTexto): string | undefined {
  if (item.ready !== undefined && item.total !== undefined) {
    return `${item.ready} de ${item.total} ${texto.unidadeDoTotal ?? 'empresas'}`;
  }
  if (item.mode) return ROTULO_DO_MODO[item.mode];
  if (item.count && texto.unidade) return plural(item.count, texto.unidade);
  return undefined;
}

function distribuicaoDeModos(item: OnboardingItem): string | undefined {
  if (!item.modeDistribution) return undefined;

  const partes = Object.entries(item.modeDistribution)
    .filter(([, quantas]) => quantas > 0)
    .map(([modo, quantas]) => {
      const nome = ROTULO_DO_MODO[modo as keyof typeof ROTULO_DO_MODO] ?? modo;
      return `${quantas} por ${nome}`;
    });
  return partes.length > 0 ? `${partes.join(' · ')}.` : undefined;
}

function vencimentos(item: OnboardingItem): string[] {
  const { expiring } = item;
  if (!expiring) return [];

  const frases: string[] = [];
  if (expiring.soonCount > 0) {
    const proximo = expiring.nextDate
      ? `, ${expiring.soonCount === 1 ? 'em' : 'o primeiro em'} ${dataBrasileira(expiring.nextDate)}`
      : '';
    const verbo = expiring.soonCount === 1 ? 'vence' : 'vencem';
    frases.push(`${expiring.soonCount} ${verbo} em até 30 dias${proximo}.`);
  }
  if (expiring.expiredCount > 0) {
    frases.push(
      `${expiring.expiredCount} ${expiring.expiredCount === 1 ? 'já venceu' : 'já venceram'}.`
    );
  }
  return frases;
}

function detalhesDoItem(item: OnboardingItem, texto: ItemTexto, perspectiva: Perspectiva): string[] {
  const frases: string[] = [];

  const distribuicao = distribuicaoDeModos(item);
  if (distribuicao) {
    frases.push(distribuicao);
  } else {
    const emVarias = item.total !== undefined && item.total > 1;
    const doEstado =
      item.state === 'pending' && emVarias && texto.pendenteEmVarias
        ? texto.pendenteEmVarias
        : texto.detalhe?.[item.state];
    if (doEstado) frases.push(escolher(doEstado, perspectiva));
  }

  for (const aviso of item.warnings) {
    const frase = FRASE_DA_RESSALVA[aviso.code];
    if (frase) frases.push(frase(aviso.count));
  }
  frases.push(...vencimentos(item));

  if (texto.soPlataforma && perspectiva !== 'plataforma') frases.push(texto.soPlataforma);

  return frases;
}

function quemFalta(item: OnboardingItem, perspectiva: Perspectiva): string | undefined {
  if (!item.missing || item.missing.length === 0) return undefined;

  const nomes = item.missing.map((e) => e.name).join(', ');
  const restantes = (item.missingTotal ?? item.missing.length) - item.missing.length;
  const fecho = restantes > 0 ? ` e mais ${restantes}.` : '.';
  const lead = perspectiva === 'plataforma' ? FRASES.maisAtrasadas : FRASES.faltamEm;
  return `${lead} ${nomes}${fecho}`;
}

interface Alvo {
  destino: Destino;
}

/** O que a linha oferece: a ação, se a pessoa pode dá-la; senão a leitura; senão nada. */
function escolherAlvo(
  item: OnboardingItem,
  texto: ItemTexto,
  manifesto: ManifestoDeAcesso | null
): { alvo?: Alvo; soConsulta: boolean } {
  if (!manifesto || item.state === 'locked') return { soConsulta: false };

  const acao = acaoDoItem(texto, item);
  const consulta = texto.consulta;
  const podeAgir = acao ? avaliar(acao.rota, manifesto, acao.exige) : undefined;
  const podeLer = consulta ? avaliar(consulta.rota, manifesto, consulta.exige) : undefined;

  const querAgir = item.state === 'pending' || item.state === 'warn';
  if (querAgir && acao && podeAgir?.liberado) return { alvo: { destino: acao }, soConsulta: false };
  if (consulta && podeLer?.liberado) {
    return {
      alvo: { destino: consulta },
      soConsulta: Boolean(acao && podeAgir?.motivo === 'permissao' && !texto.soPlataforma),
    };
  }
  return { soConsulta: false };
}

export interface ItemRowProps {
  item: OnboardingItem;
  perspectiva: Perspectiva;
  manifesto: ManifestoDeAcesso | null;
}

export const ItemRow: React.FC<ItemRowProps> = ({ item, perspectiva, manifesto }) => {
  const texto = ITENS[item.key];
  if (!texto) return null;

  const rotulo = typeof texto.rotulo === 'function' ? texto.rotulo(item) : escolher(texto.rotulo, perspectiva);
  const medida = medidaDoItem(item, texto);
  const detalhes = detalhesDoItem(item, texto, perspectiva);
  const falta = quemFalta(item, perspectiva);
  const { alvo, soConsulta } = escolherAlvo(item, texto, manifesto);
  const chip = CHIP_DO_ESTADO[item.state];

  if (soConsulta) detalhes.push(`Quem cadastra é ${QUEM_RESOLVE[perspectiva]}.`);

  return (
    <Box
      component="li"
      data-item={item.key}
      data-state={item.state}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '210px 1fr auto' },
        gap: { xs: 1, md: 2 },
        alignItems: 'start',
        py: 2,
        borderTop: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 0, pt: 0 },
      }}
    >
      <Box>
        <Chip
          icon={chip.icone}
          label={TEXTO_DO_ESTADO[item.state]}
          color={chip.cor}
          variant="outlined"
          sx={{ fontSize: '14px', fontWeight: 600 }}
        />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography component="div" sx={{ fontSize: LETRA_DO_CORPO, fontWeight: 600 }}>
          {rotulo}
          {medida && (
            <Typography
              component="span"
              sx={{ fontSize: LETRA_DO_CORPO, fontWeight: 400, color: 'text.secondary', ml: 1.5 }}
            >
              {medida}
            </Typography>
          )}
        </Typography>

        {detalhes.map((frase, posicao) => (
          <Typography
            key={`${item.key}-${posicao}`}
            sx={{ fontSize: LETRA_DO_CORPO, color: 'text.secondary', mt: 0.5 }}
          >
            {frase}
          </Typography>
        ))}

        {falta && (
          <Typography sx={{ fontSize: LETRA_DO_CORPO, mt: 0.5 }}>{falta}</Typography>
        )}
      </Box>

      {alvo && (
        <RouteActionButton
          para={alvo.destino.rota}
          exige={alvo.destino.exige}
          variant="text"
          endIcon={<GoIcon />}
          sx={{ minHeight: AREA_DE_TOQUE, fontSize: LETRA_DO_CORPO, whiteSpace: 'nowrap' }}
        >
          {alvo.destino.rotulo}
        </RouteActionButton>
      )}
    </Box>
  );
};

export default ItemRow;
